import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  DeleteOutlined,
  EditOutlined,
  FileSearchOutlined,
  ImportOutlined,
  MoreOutlined,
  CloseCircleFilled,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Popconfirm,
  Tooltip,
  Space,
  Table,
  Tag,
} from "antd";
import type { TableColumnsType } from "antd";
import { shopApi } from "api/shop.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableContent,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import dayjs from "utils/dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { GuestType } from "types/guest";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Shop } from "types/shop";
import { Todo } from "types/todo";
import { getTitle } from "utils";
import { exportExcel } from "utils/exportExcel";
import { generateGender } from "utils/generateGender";
import { $url } from "utils/url";
import { ImportRegisterModal } from "./components/ImportRegisterModal";

import { QRModal } from "./components/QRModal";
import { SubmitHistory } from "./components/SubmitHistory";
import { TodoModal } from "./components/TodoModal/TodoModal";
import { UpdateShopModal } from "./components/UpdateShopModal";
import { VerifyModal } from "./components/VerifyModal";
import "./style/todo.scss";

// import { StaffModal } from "./components/StaffModal";

const { Column } = Table;

interface PageQueryParam extends QueryParam {
  areaId?: number | null;
  businessTypeId?: number | null;
  isHasAccount?: boolean | null;
  isApproved?: boolean;
}

export const ShopNotApproved = observer(({ title }: { title: string }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<PageQueryParam>({
    page: 1,
    limit: 10,
    search: "",
    isApproved: false,
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Shop[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleQR, setVisibleQR] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Partial<Shop>>({});
  const [approveVisible, setApproveVisible] = useState(false);
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");
  const [todoVisible, setTodoVisible] = useState(false);
  const [visibleSubmitHistory, setVisibleSubmitHistory] = useState(false);
  const [visibleImport, setVisibleImport] = useState(false);
  const [updateVisible, setUpdateVisible] = useState(false);

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await shopApi.findAll(query);
    setLoading(false);
    setData(res.data.shops);
    setTotal(res.data.total);
  };

  const handleDelete = async (row: Shop) => {
    setLoading(true);
    try {
      await shopApi.delete(row.id);
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  const findOutOfDateTask = (todos: Todo[]) => {
    const outOfDateTask = todos.filter((e) => {
      return (
        e.expiredAt &&
        e.expiredAt < dayjs().endOf("day").unix() &&
        !e.isCompleted
      );
    });

    const outOfDateCounter = outOfDateTask.map((e) => {
      return dayjs().endOf("day").diff(dayjs.unix(e.expiredAt), "hour");
    });

    return outOfDateCounter;
  };

  const reportTodo = (todos: Todo[]) => {
    const doneTaskCounter = todos.filter((e) => e.isCompleted).length;
    if (todos.length)
      return (
        <Space size={5}>
          <CheckSquareOutlined style={{ color: "green" }} />{" "}
          <span>
            <b>Hoàn thành:</b>
            {doneTaskCounter + "/" + todos.length}
          </span>
        </Space>
      );
  };

  const generateGuestType = (guestType: GuestType) => {
    switch (guestType) {
      case GuestType.Chapter:
        return "Khách thăm";

      case GuestType.Guest:
        return "Khách mời";
    }
  };

  const handleExport = async () => {
    const { limit: _limit, page: _page, ...queryExport } = query;
    const res = await shopApi.findAll(queryExport);

    const shopData: Shop[] = res.data.shops;

    const orderDataExport = shopData.map((shop: Shop) => {
      const workshopInfo = shop.workshops[shop.workshops.length - 1];
      const result: any = {
        "Phân loại": generateGuestType(shop?.guestType),
        "Tên chapter": shop?.chapterName,
        "Danh xưng": generateGender(shop?.ownerGender),
        "Họ tên": workshopInfo?.name,
        "Tên doanh nghiệp": shop.name,
        "Số điện thoại": workshopInfo?.phone,
        "Lĩnh vực kinh doanh": shop.businessArea,
        "Thời gian họp": workshopInfo?.bookingDate,
      };

      return result;
    });

    exportExcel(orderDataExport, "Danh sach doanh nghiep");
  };

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  const columns = useMemo<TableColumnsType<Shop>>(
    () => [
      {
        title: "Tên doanh nghiệp",
        dataIndex: "name",
        width: 260,
        key: "username",
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <INA
              image={record?.customer?.avatar ? $url(record.customer.avatar) : undefined}
              name={record.name || "--"}
              additional={record.businessArea || "--"}
            />
            {record.guestType === GuestType.Chapter ? (
              <Tag color="blue">Khách thăm</Tag>
            ) : record.guestType === GuestType.Guest ? (
              <Tag color="cyan">Khách mời</Tag>
            ) : null}
          </div>
        ),
      },
      {
        title: "Lĩnh vực kinh doanh",
        key: "businessType",
        width: 220,
        render: (_, record) => <TableContent>{record.businessArea || "--"}</TableContent>,
      },
      {
        title: "Thông tin",
        dataIndex: "address",
        key: "phone",
        width: 260,
        render: (_, record) => (
          <div className="space-y-1">
            <TableContent>
              Người tham dự: {record?.workshops[record.workshops.length - 1]?.name || "--"}
            </TableContent>
            {record.chapterName ? (
              <TableContent>Tên chapter: {record.chapterName}</TableContent>
            ) : null}
            <TableContent>
              Số điện thoại: {record?.workshops[record.workshops.length - 1]?.phone || "--"}
            </TableContent>
          </div>
        ),
      },
      {
        title: "Ngày tham dự",
        key: "bookingDate",
        width: 130,
        render: (_, record) => (
          <span>
            {dayjs(record.workshops[record.workshops.length - 1]?.bookingDate).format(
              "DD/MM/YYYY",
            )}
          </span>
        ),
      },
      {
        width: 220,
        title: "Log công việc",
        key: "todo",
        render: (_, record) => {
          const deadlines = findOutOfDateTask(record.todos);
          return (
            <div className="flex flex-col items-center">
              <p>
                <span>{reportTodo(record.todos)}</span>
              </p>
              <Space direction="vertical">
                {deadlines.map((time: number) =>
                  time < 24 ? (
                    <Tag color="red">
                      <b>Quá hạn: </b>-{time} tiếng
                    </Tag>
                  ) : (
                    <Tag color="red">
                      <b>Quá hạn: </b>-{Math.round(time / 24)} ngày
                    </Tag>
                  ),
                )}
              </Space>
            </div>
          );
        },
      },
      {
        title: "Thao tác",
        key: "action",
        width: 170,
        align: "center",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Lịch sử điểm danh">
              <ActionButton
                aria-label="Lịch sử điểm danh"
                onClick={() => {
                  setSelectedShop(record);
                  setVisibleSubmitHistory(true);
                }}
              >
                <FileSearchOutlined style={{ color: "#2563eb" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Log công việc">
              <ActionButton
                aria-label="Log công việc"
                onClick={() => {
                  setSelectedShop(record);
                  setTodoVisible(true);
                }}
              >
                <ClockCircleOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Duyệt">
              <ActionButton
                aria-label="Duyệt"
                onClick={() => {
                  setSelectedShop(record);
                  setApproveVisible(true);
                }}
              >
                <CheckCircleOutlined style={{ color: "#22c55e" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  setSelectedShop(record);
                  setUpdateVisible(true);
                  setModalStatus("update");
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Xóa">
              <Popconfirm
                cancelText="Không"
                okText="Xóa"
                onConfirm={() => handleDelete(record)}
                title="Xác nhận xóa?"
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>
          </div>
        ),
      },
    ],
    [query.limit, query.page],
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            actions={
              <>
                <AppButton
                  onClick={() => {
                    setVisibleImport(true);
                  }}
                  tone="primary"
                  icon={<ImportOutlined />}
                >
                  Import Excel
                </AppButton>
                <Popconfirm
                  title="Bạn có muốn xuất danh sách này ra file excel"
                  onConfirm={handleExport}
                >
                  <AppButton tone="primary" icon={<ImportOutlined />}>
                    Xuất file Excel
                  </AppButton>
                </Popconfirm>
              </>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Shop>
            loading={loading}
            pagination={false}
            rowKey="id"
            dataSource={data}
            columns={columns}
            scroll={{ x: 1200 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            totalLabel="doanh nghiệp"
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />
      {todoVisible && (
        <TodoModal
          todoVisible={todoVisible}
          onClose={() => setTodoVisible(false)}
          shopId={selectedShop.id}
          onSubmitOk={fetchData}
        />
      )}
      {updateVisible && (
        <UpdateShopModal
          visible={updateVisible}
          status={modalStatus}
          onClose={() => setUpdateVisible(false)}
          onSubmitOk={fetchData}
          shopInfo={selectedShop}
        />
      )}
      {approveVisible && (
        <VerifyModal
          visible={approveVisible}
          onClose={() => setApproveVisible(false)}
          onSubmitOk={fetchData}
          selectedShop={selectedShop}
        />
      )}

      {visibleQR && (
        <QRModal visible={visibleQR} onClose={() => setVisibleQR(false)} />
      )}

      <ImportRegisterModal
        visible={visibleImport}
        onClose={() => setVisibleImport(false)}
        onSubmitOk={() =>
          setTimeout(() => {
            fetchData();
          }, 2000)
        }
      />

      {visibleSubmitHistory && (
        <SubmitHistory
          visible={visibleSubmitHistory}
          selectedShop={selectedShop}
          onCancel={() => setVisibleSubmitHistory(false)}
        />
      )}
    </div>
  );
});
